import { Observable, of } from 'rxjs';
import { shareReplay, switchMap, tap } from 'rxjs/operators';

import { RestService } from './rest.service';

// Lists that depend on a transaction date (e.g. shareholder names on 456/457 analytics, #12574).
// Keeps the last few lists for a few minutes, so new accounts still show up without a reload.
// The undated list is used only while the gateway does not know the date argument yet (saliente
// deployed before the backend); any other error is shown as usual. Failures and fallbacks are not cached.
export class DatedListCache<T> {
	private entries = new Map<string, { list: Observable<T>, expiresAt: number }>();

	constructor(private readonly restService: RestService, private readonly maxEntries = 10, private readonly ttlMs = 5 * 60 * 1000) { }

	public get(cacheKey: string,
		queryDated: (onError: (error: any) => void) => Observable<T>,
		queryUndated: () => Observable<T>): Observable<T> {
		const now = Date.now(),
			cached = this.entries.get(cacheKey);
		if (cached && cached.expiresAt > now) {
			return cached.list;
		}
		this.entries.delete(cacheKey);
		if (this.entries.size >= this.maxEntries) {
			this.entries.delete(this.entries.keys().next().value);
		}

		let unknownDateArgument = false;
		const list = queryDated((error: any) => {
			unknownDateArgument = isUnknownArgumentError(error);
			if (!unknownDateArgument) {
				this.restService.handleError(error);
			}
		}).pipe(
			switchMap((result: T) => unknownDateArgument ? queryUndated() : of(result)),
			tap((result: T) => {
				if (!result || unknownDateArgument) {
					this.entries.delete(cacheKey);
				}
			}),
			shareReplay(1));
		this.entries.set(cacheKey, { list, expiresAt: now + this.ttlMs });
		return list;
	}
}

// GraphQL validation error for an argument the schema does not have; depending on the gateway it comes
// as graphQLErrors or inside the 400 response of networkError
function isUnknownArgumentError(error: any): boolean {
	const errors: any[] = [...(error?.graphQLErrors || []), ...(error?.networkError?.result?.errors || [])];
	return errors.some((e: any) => /Unknown argument/i.test(e?.message || ''));
}
